import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import BusinessIcon from '@mui/icons-material/Business';
import { useSurveyRuntimeApi } from '../context/SurveyRuntimeApiContext';
import { getApiErrorMessage } from '../api/runtimeErrors';
import { useSurveyUiToast } from '../hooks/useSurveyUiToast';

/**
 * Multi-select of SDA client tenants (GET /admin/clients).
 * value / onChange use [{ client_tenant_id, client_name }].
 */
export function ClientMultiSelect({ value, onChange, disabled, label = 'Clients', autoFocus = false }) {
  const { apiFetch } = useSurveyRuntimeApi();
  const [options, setOptions] = useState(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await apiFetch('/admin/clients');
        if (!cancelled) setOptions(data);
      } catch {
        if (!cancelled) setOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiFetch]);

  return (
    <Autocomplete
      multiple
      options={options ?? []}
      loading={options === null}
      disabled={disabled}
      value={value}
      onChange={(e, next) => onChange(next)}
      getOptionLabel={(o) => o.client_name || ''}
      isOptionEqualToValue={(o, v) =>
        (o.client_tenant_id || '').toUpperCase() === (v.client_tenant_id || '').toUpperCase()
      }
      renderTags={(selected, getTagProps) =>
        selected.map((option, index) => (
          <Chip
            size="small"
            label={option.client_name}
            {...getTagProps({ index })}
            key={option.client_tenant_id}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          autoFocus={autoFocus}
          placeholder={value.length === 0 ? 'Search clients…' : undefined}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {options === null ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

/**
 * Compact "Clients" row for the survey builder: chips + edit dialog.
 * Saves via PUT /admin/surveys/{id}/clients (children of recurring surveys
 * follow automatically server-side).
 */
export default function SurveyClientsEditor({ surveyId, clients, onSaved, disabled }) {
  const { apiFetch } = useSurveyRuntimeApi();
  const { showError } = useSurveyUiToast();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(clients || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(clients || []);
  }, [clients]);

  async function save() {
    setSaving(true);
    try {
      const r = await apiFetch(`/admin/surveys/${surveyId}/clients`, {
        method: 'PUT',
        body: JSON.stringify({
          clients: draft.map((c) => ({
            client_tenant_id: c.client_tenant_id,
            client_name: c.client_name,
          })),
        }),
      });
      setOpen(false);
      onSaved?.(r.clients || []);
    } catch (e) {
      showError(getApiErrorMessage(e, 'Failed to update clients.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <BusinessIcon fontSize="small" color="action" />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Clients:
        </Typography>
      </Box>
      {(clients || []).length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          None — visible to your tenant only
        </Typography>
      ) : (
        (clients || []).map((c) => (
          <Chip key={c.client_tenant_id} size="small" label={c.client_name} />
        ))
      )}
      {!disabled && (
        <Button
          size="small"
          startIcon={<EditIcon fontSize="small" />}
          onClick={() => setOpen(true)}
          sx={{ textTransform: 'none' }}
        >
          Edit
        </Button>
      )}

      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Survey clients</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This survey is created for the selected clients — each of them sees it in their own
            workspace. Recurring cycles follow the same client list automatically.
          </Typography>
          <ClientMultiSelect value={draft} onChange={setDraft} disabled={saving} autoFocus />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void save()}
            disabled={saving}
            sx={{
              textTransform: 'none',
              backgroundColor: '#000000',
              '&:hover': { backgroundColor: '#333333' },
            }}
          >
            {saving ? 'Saving…' : 'Save clients'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
